import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { ArrowLeft, Plus, RefreshCw } from 'lucide-react';
import { ordersApi, allocationsApi, packagingContainersApi, type WorkOrderDto } from '../../services/api';
import type { PalletPreviewResponse, PalletPreviewDto, PackagingContainer } from '../../types/loading-plan';
import { Button } from '../../components/ui/button';
import { PalletColumn } from './components/PalletColumn';

export const WarehouseOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<WorkOrderDto | null>(null);
  const [layout, setLayout] = useState<PalletPreviewResponse | null>(null);
  const [containers, setContainers] = useState<PackagingContainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingPallet, setCreatingPallet] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    packagingContainersApi.getAll().then(setContainers).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setError(null);
      const [o, p] = await Promise.all([
        ordersApi.getById(id),
        ordersApi.getSavedPallet(id).catch(() => ({ layout: null })),
      ]);
      setOrder(o);
      setLayout(p?.layout ?? null);
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar la comanda.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Refresco periódico (20 s) para que dos mozos en puestos distintos vean los
  // palets y allocations del otro sin tener que recargar manualmente.
  useEffect(() => {
    if (!id) return;
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, [id, load]);

  const handleCreatePallet = async () => {
    if (!id) return;
    try {
      setCreatingPallet(true);
      await allocationsApi.createManualPallet(id);
      await load();
    } catch (e: any) {
      setError(e?.message || 'No se pudo crear el palet.');
    } finally {
      setCreatingPallet(false);
    }
  };

  // Suma de bultos por línea de comanda a lo largo de TODOS los palets.
  // El backend ya nos devuelve order.lines[i].allocations con todo lo asignado.
  const coverageByLine = useMemo(() => {
    const out: Record<string, { assigned: number; required: number; name: string; kg: number }> = {};
    if (!order?.lines) return out;
    for (const line of order.lines) {
      const assigned = (line.allocations ?? []).reduce((s, a) => s + a.bultos, 0);
      const kg = (line.allocations ?? []).reduce((s, a) => s + a.quantity, 0);
      out[line.id] = {
        assigned,
        required: line.quantity ?? 0,
        name: line.productName ?? '—',
        kg,
      };
    }
    return out;
  }, [order]);

  const pallets: PalletPreviewDto[] = layout?.pallets ?? [];

  if (loading) {
    return (
      <div className="p-8 text-slate-500">Cargando…</div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <p className="text-red-700">{error}</p>
        <Button variant="outline" onClick={() => navigate('/comandas')} className="mt-4">
          <ArrowLeft className="mr-2" /> Volver
        </Button>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header con cobertura agregada — tipografía grande para ver de lejos */}
      <header className="border-b border-slate-200 bg-white px-6 py-4 shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Link to="/comandas" className="text-slate-500 hover:text-slate-800">
                <ArrowLeft size={20} />
              </Link>
              <h1 className="text-3xl font-bold text-slate-900">
                {order.customerName ?? '—'}
              </h1>
              <span className="text-xl text-slate-500 font-mono">
                #{order.id.slice(0, 8)}
              </span>
            </div>
            {order.orderDate && (
              <p className="text-sm text-slate-500 mt-1 ml-7">
                Reparto: {new Date(order.orderDate).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load}>
              <RefreshCw size={16} /> Recargar
            </Button>
            <Button onClick={handleCreatePallet} disabled={creatingPallet} className="bg-green-600 hover:bg-green-700 text-white">
              <Plus size={16} /> {creatingPallet ? 'Creando…' : 'Nuevo palet'}
            </Button>
          </div>
        </div>

        {/* Cobertura por línea, tipografía grande */}
        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 ml-7">
          {Object.entries(coverageByLine).map(([lineId, c]) => {
            const complete = c.assigned >= c.required && c.required > 0;
            const over = c.assigned > c.required;
            const color = over ? 'text-red-600' : complete ? 'text-green-700' : 'text-slate-700';
            return (
              <div key={lineId} className="flex items-baseline gap-2">
                <span className="text-base text-slate-500 uppercase tracking-wide">{c.name}</span>
                <span className={`text-2xl font-bold tabular-nums ${color}`}>
                  {c.assigned}/{c.required}
                </span>
                {complete && !over && <span className="text-green-700 text-xl">✓</span>}
              </div>
            );
          })}
        </div>
      </header>

      {/* Columnas de palets */}
      <main className="flex-1 overflow-x-auto overflow-y-hidden p-6">
        {pallets.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <p className="text-2xl text-slate-400 mb-4">Aún no hay palets en esta comanda.</p>
              <Button size="lg" onClick={handleCreatePallet} disabled={creatingPallet} className="bg-green-600 hover:bg-green-700 text-white">
                <Plus size={20} /> Crear el primer palet
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-4 h-full">
            {pallets.map(p => (
              <PalletColumn
                key={p.id}
                orderId={order.id}
                pallet={p}
                orderLines={order.lines ?? []}
                containers={containers}
                onChanged={load}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default WarehouseOrderPage;
